import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import Panel from "../../components/brand/Panel";
import {
  createFiscalYear,
  getBudgets,
  getFiscalYears,
  updateFiscalYear,
} from "../../api/financeApi";
import { getApiErrorMessage } from "../../utils/concurrency";
import { formatDateUk } from "../../utils/dateDisplay";

const toIsoDate = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;

const suggestedFiscalYearDates = (startDay, startMonth, existingYears) => {
  if (!startDay || !startMonth) return { start_date: "", end_date: "" };

  const today = new Date();
  const day = Number(startDay);
  const month = Number(startMonth);
  const dateForYear = (year) => {
    const lastDay = new Date(year, month, 0).getDate();
    return new Date(year, month - 1, Math.min(day, lastDay));
  };
  const existingStartDates = new Set(existingYears.map((year) => year.start_date));
  let year = today.getFullYear();
  if (dateForYear(year) > today) year -= 1;
  while (existingStartDates.has(toIsoDate(dateForYear(year)))) year += 1;

  const start = dateForYear(year);
  const end = dateForYear(year + 2);
  end.setDate(end.getDate() - 1);
  return { start_date: toIsoDate(start), end_date: toIsoDate(end) };
};

export default function FiscalYearsPanel({
  propertyId,
  financialYearStartDay,
  financialYearStartMonth,
  onMessage,
  onError,
}) {
  const [fiscalYears, setFiscalYears] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settingCurrentId, setSettingCurrentId] = useState(null);
  const [form, setForm] = useState({ start_date: "", end_date: "" });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      onError?.("");
      const [fyRes, budgetRes] = await Promise.all([
        getFiscalYears(propertyId),
        getBudgets(propertyId, 1, { page_size: 100 }),
      ]);
      setFiscalYears(fyRes.data.results || []);
      setBudgets(budgetRes.data.results || []);
    } catch (e) {
      onError?.(getApiErrorMessage(e, "Failed to load fiscal years."));
    } finally {
      setLoading(false);
    }
  }, [propertyId, onError]);

  useEffect(() => {
    load();
  }, [load]);

  const budgetsByFy = useMemo(() => {
    const map = {};
    budgets.forEach((budget) => {
      const fyId = budget.fiscal_year?.id;
      if (!fyId) return;
      if (!map[fyId]) map[fyId] = [];
      map[fyId].push(budget);
    });
    return map;
  }, [budgets]);

  const handleCreate = async () => {
    if (!form.start_date || !form.end_date) {
      onError?.("Start and end dates are required.");
      return;
    }
    try {
      setSaving(true);
      onError?.("");
      await createFiscalYear(propertyId, form);
      setDialogOpen(false);
      setForm({ start_date: "", end_date: "" });
      onMessage?.("Fiscal year created.");
      await load();
    } catch (e) {
      onError?.(getApiErrorMessage(e, "Failed to create fiscal year."));
    } finally {
      setSaving(false);
    }
  };

  const handleOpenCreateDialog = () => {
    setForm(
      suggestedFiscalYearDates(
        financialYearStartDay,
        financialYearStartMonth,
        fiscalYears
      )
    );
    setDialogOpen(true);
  };

  const handleSetCurrent = async (fiscalYear) => {
    try {
      setSettingCurrentId(fiscalYear.id);
      onError?.("");
      await updateFiscalYear(propertyId, fiscalYear.id, { is_current: true });
      onMessage?.("Current fiscal period updated.");
      await load();
    } catch (e) {
      onError?.(getApiErrorMessage(e, "Failed to set current fiscal period."));
    } finally {
      setSettingCurrentId(null);
    }
  };

  return (
    <>
      <Panel
        title="Fiscal years"
        noBodyPadding={false}
        right={
          <Button size="small" startIcon={<AddIcon />} onClick={handleOpenCreateDialog}>
            Add fiscal year
          </Button>
        }
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Each property has its own financial year dates. Mark one period as current — service
          charge recalculation uses the budget for that period.
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Current</TableCell>
                <TableCell>Period</TableCell>
                <TableCell>Start</TableCell>
                <TableCell>End</TableCell>
                <TableCell>Budgets</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5}>Loading…</TableCell>
                </TableRow>
              ) : fiscalYears.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>No fiscal years defined yet.</TableCell>
                </TableRow>
              ) : (
                fiscalYears.map((fy) => (
                  <TableRow key={fy.id} selected={fy.is_current}>
                    <TableCell padding="checkbox">
                      <Radio
                        checked={Boolean(fy.is_current)}
                        disabled={settingCurrentId === fy.id}
                        onChange={() => handleSetCurrent(fy)}
                        inputProps={{ "aria-label": `Set ${fy.label} as current` }}
                      />
                    </TableCell>
                    <TableCell>{fy.label}</TableCell>
                    <TableCell>{formatDateUk(fy.start_date)}</TableCell>
                    <TableCell>{formatDateUk(fy.end_date)}</TableCell>
                    <TableCell>
                      {(budgetsByFy[fy.id] || []).length === 0
                        ? "—"
                        : (budgetsByFy[fy.id] || []).map((b) => b.name).join(", ")}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Panel>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add fiscal year</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <TextField
            label="Start date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={form.start_date}
            onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
          />
          <TextField
            label="End date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={form.end_date}
            onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreate} disabled={saving}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
